import { useNavigate } from "react-router-dom";
import { Button } from "../Catalyst/button";
import { Table, TableHead, TableRow, TableHeader, TableBody, TableCell } from "../Catalyst/table";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { useEffect, useState } from "react";
import { fetchSearchedUsersListAsync } from "../../store/thunks/UsersThunk";
import useOrgId from "../../hooks/useOrgId";

export const USER_STATUS = {
  ACTIVE: "ACTIVE",
  DEACTIVATED: "DEACTIVATED",
  INVITED: "INVITED",
  SIGNED_UP: "SIGNED_UP",
} as const;

interface User {
  id: string;
  organization_id: number;
  email: string;
  email_verified: boolean;
  first: string;
  middle: string | null;
  last: string;
  mobile_phone: string | null;
  job_title: string | null;
  user_role: string;
  created_at: string;
  updated_at: string;
  onboarding_state: string | null;
  terms_accepted_at: string | null;
  deactivated_at: string | null;
  invitation_sent_at: string | null;
  external_unique_id: string;
  is_active: boolean;
  user_status: string | null;
  user_status_date: string | null;
}

interface UsersTableProps {
  searchText?: string | undefined;
}

const UserListTable = ({ searchText }: UsersTableProps) => {
  const selectedOrgId = useOrgId();

  const { total_pages, filteredUsers } = useSelector(
    (state: RootState) => state.UserStore
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage] = useState<number>(10);
  const dispatch = useDispatch<AppDispatch>();

  //rerender if selectedOrgId changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrgId]);

  useEffect(() => {
    if (!selectedOrgId) return;

    dispatch(
      fetchSearchedUsersListAsync({
        page: currentPage,
        per_page: perPage,
        orgId: selectedOrgId,
        searchString: searchText || "",
      })
    );
  }, [currentPage, perPage, selectedOrgId, searchText, dispatch]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const STATUS_STYLES: Record<string, { displayName: string; colorClass: string }> = {
    [USER_STATUS.ACTIVE]: { displayName: "Active", colorClass: "text-green-600" },
    [USER_STATUS.INVITED]: { displayName: "Invited", colorClass: "text-text-primary-900 opacity-50" },
    [USER_STATUS.DEACTIVATED]: { displayName: "Deactivated", colorClass: "text-red-400" },
    [USER_STATUS.SIGNED_UP]: { displayName: "Signed Up", colorClass: "text-yellow-500" },
  };

  const getStatusText = (user: User) => {
    const baseStyles = "font-inter text-sm font-medium leading-5";
    const status = user.user_status || USER_STATUS.ACTIVE;
    const config = STATUS_STYLES[status] || STATUS_STYLES[USER_STATUS.ACTIVE];
    const dateLabel = user.user_status_date ? formatDate(user.user_status_date) : "";

    if (status === USER_STATUS.ACTIVE) {
      return (
        <span className={`${baseStyles} ${config.colorClass}`}>
          {dateLabel ? `Active since ${dateLabel}` : "Active"}
        </span>
      );
    }
    return (
      <span className={`${baseStyles} ${config.colorClass}`}>
        {dateLabel ? `${config.displayName} on ${dateLabel}` : config.displayName}
      </span>
    );
  };

  const getUserNameStyle = (user: User) => {
    if (user.user_status === USER_STATUS.DEACTIVATED || user.user_status === USER_STATUS.INVITED) {
      return "font-medium opacity-50";
    }
    return "font-medium";
  };


  const navigate = useNavigate();

  return (
    <div className="px-6">
      {
        selectedOrgId ? 
             <Table>
        <TableHead>
          <TableRow className="bg-[#F9FAFB] border border-x-[#EAECF0]">
            <TableHeader>Name</TableHeader>
            <TableHeader className="w-[150px]" />
            <TableHeader>Status</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredUsers.length > 0 && filteredUsers.map((user: User) => (
            <TableRow key={user.id}>
              <TableCell className={getUserNameStyle(user)}>
                {user.first} {user.last}
              </TableCell>
              <TableCell />
              <TableCell>
                {getStatusText(user)}
              </TableCell>
              <TableCell>
                <Button 
                  outline 
                  onClick={() => navigate(`/user-profile/${selectedOrgId}/${user.id}`)}
                  className="bg-textColor2"
                >
                  <span className="desktext13">View Activity</span>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      : 
          <div className="h-[692px] border-b-[1px] border-[#EAECF0] inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-[#667085] text-center max-w-md">
              No Organization Selected. Select an Organization from the
              Organizations tab to populate the user list.
            </p>
          </div>
      }
 

      <div className="flex items-center justify-between px-2 py-3 text-sm text-zinc-600">
        <div>
          Page {currentPage} of {total_pages || 1}
        </div>
        <div className="flex gap-2 items-center">
          <Button
            outline
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </Button>

          {(() => {
            const pages: React.ReactNode[] = [];
            const total = total_pages;
            const current = currentPage;

            const addPage = (num: number) => {
              const isActive = current === num;
              pages.push(
                <button
                  key={num}
                  onClick={() => setCurrentPage(num)}
                  style={isActive ? { backgroundColor: 'rgba(9,9,11,0.05)' } : {}}
                  className="min-w-[36px] h-9 px-3 rounded-lg text-sm font-semibold text-zinc-950 hover:bg-zinc-950/[2.5%]"
                >
                  {num}
                </button>
              );
            };

            if (total <= 5) {
              for (let i = 1; i <= total; i++) addPage(i);
            } else {
              addPage(1);

              if (current > 3) {
                pages.push(<span key="left-ellipsis" className="px-1">...</span>);
              }

              for (let p = current - 1; p <= current + 1; p++) {
                if (p > 1 && p < total) addPage(p);
              }

              if (current < total - 2) {
                pages.push(<span key="right-ellipsis" className="px-1">...</span>);
              }

              addPage(total);
            }

            return pages;
          })()}

          <Button
            outline
            disabled={currentPage === total_pages || total_pages === 0}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserListTable;
